import {
	DecisionKind,
	AttributeType,
	ChoiceValueDecisionState,
} from '@viamedici-spc/configurator-ts';
import throttle from 'throttleit';

import {
	setBuriedBaseSize,
	setHousingSize, setMaxHeight, setNumberOfHeads, setOlivioData,
	setOlivioHeadHousingSize,
	setOlivioHeadLightDistribution,
	setOlivioHeadsData,
	setPoleHeight, setSecondDoor,
	setSelectedHead, setSeparateBuriedBase
} from './olivioSlice';
import {getValueOfFeature, spcExpliciteChoiceDecision} from '../util/values';
import {setProductModel, storeConfiguration} from '../slices/configurationSlice';
import {
	selectOlivioBracket,
	selectOlivioDesign,
	selectOlivioDesignData,
	selectOlivioDesignIsBollard,
	selectOlivioDesignIsFloracion,
	selectOlivioHousingColourInlay,
	selectOlivioHousingColourInlayHexValue,
	selectOlivioHousingSize,
	selectOlivioHousingSizeInclCustom,
	selectOlivioLayout,
	selectOlivioMaxHeight,
	selectOlivioNumberOfHeadsValue,
	selectOlivioOffsetFromTop
} from './olivioSelectors';
import {selectSpcSession} from '../selectors';
import {stringToAttributeId} from '../components/spc/util/spcUtils';
import {olivioDesignOptions} from './olivioModel';
import {getFilteredDecisions} from '../util/getFilteredDecisions';


function syncSpcToOlivio(store) {
	const state = store.getState();
	// Copy spcValues into slice
	const heads = [];
	for (let i = 7; i >= 0; i--) {
		const spcSession = state.spc.sessions['olivio_' + i];
		const currentDecisions = spcSession?.getDecisions(DecisionKind.Explicit);
		const spcConfiguration = state.spc.spcs['olivio_' + i]?.configuration;
		//console.log('olivioMiddleWare syncSpcToOlivio currentDecisions', 'olivio_' + i, spcSession, spcConfiguration, currentDecisions);

		if (spcConfiguration) {
			const head = {
				housingSize: getValueOfFeature('root::HS_HousingSize', spcConfiguration),
				dimmingLevel: getValueOfFeature('root::Heads::Head::Type::Luminaire::LightEngine::LE_DimmingLevel', spcConfiguration),
				colourTemperature: getValueOfFeature('root::LE_ColourTemperature_1', spcConfiguration),
				lightDistribution: getValueOfFeature('root::LE_LightDistribution_1', spcConfiguration),
				reflectorColor: getValueOfFeature('root::LE_ReflectorColour_1', spcConfiguration),
				accessories:  getValueOfFeature('root::LE_Accessories_1', spcConfiguration),
				spcDecisions: currentDecisions
			};
			heads.push(head);
		}
	}
	store.dispatch(setOlivioHeadsData({heads: heads}));
}
const throttledStore  = throttle((s) => {
	s.dispatch(storeConfiguration());
}, 1500);

export const olivioMiddleware = store => next => async action => {
	const result = next(action);
	const s = store.getState();

	// ignore, if not Olivio
	if (s.status.family !== 'Olivio') return result;

	// when default session is updated...
	/*
	if (action.type === 'spc/updateConfiguration') {
		console.log('olivioMiddleWare spc updateConfiguration');
		// ...transfer changes of default head to single heads
		const changes = action.payload.changes.attributes.changed;
		const spcConfiguration = store.getState().spc.spcs.default?.configuration;

		//const housingSizeChanged = changes.some(c => c.key === 'root::HS_HousingSize');
		const colourTemperatureChanged = changes.some(c => c.key === 'root::LE_ColourTemperature_1');
		const lightDistributionChanged = changes.some(c => c.key === 'root::LE_LightDistribution_1');

		const heads = Array.from({ length: 8 }, (_, i) => ({
			//...housingSizeChanged && { housingSize: getValueOfFeature('root::HS_HousingSize', spcConfiguration) },
			...colourTemperatureChanged && { colourTemperature: getValueOfFeature('root::LE_ColourTemperature_1', spcConfiguration) },
			...lightDistributionChanged && { lightDistribution: getValueOfFeature('root::LE_LightDistribution_1', spcConfiguration) }
		}));
		store.dispatch(setOlivioHeadsData({heads: heads}));
	}
	 */
	// enforce default state on start, when configuration is stored without the manual defaults
	if (action.type === 'configuration/store/fulfilled' ) {
	const spcSession = selectSpcSession(s);
	if (selectOlivioHousingSize(s) === null) {
		// set matching default values for Olivio
		const headAttributes= [
			{id: 'root::HS_HousingSize', value: 'OlivioGrande'},
			{id: 'root::LE_ColourTemperature_1', value: '3000K'},
			{id: 'root::LE_LightDistribution_1', value: 'Asymmetric'},
			{id: 'root::Heads::Head::Type::Luminaire::LightEngine::LE_DimmingLevel', value: 'Max'},
			{id: 'root::EB_ConstantLumenTechnology_1', value: 'None'},
			{id: 'root::EB_Zhaga_1', value: 'None'},
			{id: 'root::EB_ElectricalProtectionClass_1', value: 'Class2'},
			{id: 'root::EB_Control_1', value: 'NoDim'},
			{id: 'root::LE_Accessories_1', value: 'None'}
		];
		const spcAttributes	= headAttributes.map((attribute) => spcExpliciteChoiceDecision(attribute.id, attribute.value)) ?? [];
		await spcSession.makeManyDecisions(spcAttributes, {type: 'KeepExistingDecisions'});
		// syncSpcToOlivio();
		}
	}
	if (action.type === 'status/setSection') {
		if (action.payload.currentSection === 'olivioHeadDetail') {
			// always start with all-head settings when switching to heads section
			store.dispatch(setSelectedHead({selectedHead: null}));
		}
	}
	if (action.type === 'olivio/setHousingSize') {
		// transfer housing size to heads
		const heads = Array.from({ length: 8 }, (_, i) => ({
			... { housingSize: action.payload.id }
		}));
		store.dispatch(setOlivioHeadsData({heads: heads}));

		// set housing size in SPC
		const spcSession = selectSpcSession(s);
		spcSession.makeDecision({
			type: AttributeType.Choice, attributeId: stringToAttributeId('root::HS_HousingSize'),
			choiceValueId: action.payload.id, state: ChoiceValueDecisionState.Included
		});
	}

	if (action.type === 'olivio/setPoleHeight') {
		// set HousingSize for Bollard to Piccolo or Medio depending on height
		if (selectOlivioDesignIsBollard(s)) {
			store.dispatch(setHousingSize({id: action.payload.id === '1' ? 'OlivioPiccolo' : 'OlivioMedio'}));
		}
		store.dispatch(setMaxHeight({value: +action.payload.id - selectOlivioOffsetFromTop(s)}));
	}
	if (action.type === 'olivio/setBracket') {
		// reset size to OlivioGrande if bracket Sistema 1/2 and housingSize is Piccolo
		const newDesign = action.payload.id;
		// ensure no picollo on sistema1 and sistema2
		if ((newDesign === 'sistema1' || newDesign === 'sistema2') &&
			(selectOlivioHousingSizeInclCustom(s) === 'OlivioPiccolo' || selectOlivioHousingSizeInclCustom(s) === 'custom')) {
			store.dispatch(setHousingSize({id: 'OlivioGrande'}));
		}
		// ensure maximal head number on cluster layout
		if (selectOlivioLayout(s) === 'cluster') {
			if ((newDesign === 'sistema1' || newDesign === 'sistema2') && selectOlivioNumberOfHeadsValue(s) > 5) {
				setNumberOfHeads({value: 5});
			} else if (newDesign === 'sistema3' && selectOlivioNumberOfHeadsValue(s) > 4) {
				setNumberOfHeads({value: 4});
			}
		}
	}
	if (action.type === 'olivio/setBase') {
		const newBase = action.payload.id;
		if (newBase !== 'FlangePlateDE') {
			store.dispatch(setSeparateBuriedBase({id: 'none'}));
			store.dispatch(setBuriedBaseSize({id: '1200'}));
		}
		if (newBase !== 'buriedbase') {
			store.dispatch(setSeparateBuriedBase({id: 'none'}));
			store.dispatch(setBuriedBaseSize({id: null}));
		}
	}
	if (action.type === 'olivio/setOlivioDesign') {
		const newDesign = action.payload.id;
		const newModel = olivioDesignOptions.find(d => d.id === newDesign).model;
		console.log('olivio/setOlivioDesign', newDesign, newModel, s.olivio.data.design, selectOlivioDesign(s));
		// switch model if necessary
		if (newDesign !== selectOlivioDesign(s)) {
			//await store.dispatch(setProductModel({ productModel: newModel}));
		}

		// reset size to OlivioGrande if candelabra is set and housingSize is Piccolo
		if (newDesign === 'sistema' && selectOlivioHousingSize(s) === 'OlivioPiccolo') {
			store.dispatch(setHousingSize({id: 'OlivioGrande'}));
		}
		if (newDesign === 'candelabra' && selectOlivioHousingSize(s) === 'OlivioPiccolo') {
			store.dispatch(setHousingSize({id: 'OlivioGrande'}));
		}
		if (newDesign === 'candelabra') {
			store.dispatch(setSecondDoor({id: 'none'}));
		}
		if (newDesign === 'bollard') {
			// reset default setup for bollards
			store.dispatch(setPoleHeight({id: 1}));
			store.dispatch(setHousingSize({id: 'OlivioPiccolo'}));
			store.dispatch(setOlivioHeadLightDistribution({instance: 0, id: 'Asymmetric'}));
			// set Accessories to none
			const spcSession = selectSpcSession(s);
			spcSession.makeDecision({
				type: AttributeType.Choice, attributeId: stringToAttributeId('root::LE_Accessories_1'),
				choiceValueId: 'None', state: ChoiceValueDecisionState.Included
			});
		}
		if (newDesign === 'universal') {
			store.dispatch(setHousingSize({id: 'OlivioGrande'}));
			store.dispatch(setOlivioHeadLightDistribution({instance: 0, id: 'Asymmetric'}));
		}
	}

	// additional actions for olivio that require config storage
	if (action.type === 'olivio/setOlivioDesign' ||
		action.type === 'olivio/setPoleHeight' ||
		action.type === 'olivio/setNumberOfHeads' ||
		action.type === 'olivio/setLayout' ||
		action.type === 'olivio/setSeparateBuriedBase' ||
		action.type === 'olivio/setSecondDoor' ||
		action.type === 'olivio/setFuseBox' ||
		action.type === 'olivio/setPoleDiameter' ||
		action.type === 'olivio/setPoleMaterial' ||
		action.type === 'olivio/setWoodType' ||

		action.type === 'olivio/setMaxHeight' ||
		action.type === 'olivio/setMinHeight' ||
		action.type === 'olivio/setMaxAngle' ||
		action.type === 'olivio/setMinAngle' ||
		action.type === 'olivio/setSelectedHeadHeight' ||
		action.type === 'olivio/setSelectedHeadAngle' ||

		action.type === 'olivio/setHousingSize' ||
		action.type === 'olivio/setBracket' ||
		action.type === 'olivio/setOlivioHeadsData' ||

		action.type === 'olivio/setOlivioHeadHeight' ||
		action.type === 'olivio/setOlivioHeadAngle' ||
		action.type === 'olivio/setOlivioHeadType' ||
		action.type === 'olivio/setOlivioHeadColourTemperature' ||
		action.type === 'olivio/setOlivioHeadLightDistribution' ||
		action.type === 'olivio/setOlivioHeadCap' ||
		action.type === 'olivio/setOlivioHeadHousingSize' ||
		action.type === 'olivio/setOlivioHeadColourTemperature' ||
		action.type === 'olivio/setOlivioCandelabraTop' ||
		action.type === 'olivio/setBase'

	) {
		throttledStore(store);
	}

	if (action.type === 'shortNumber/loadConfigByShortNumberLoading/fulfilled') {
		const loadedConfig = s.shortNumber.data.loadedConfig.configurationData;
		await store.dispatch(setOlivioData(
			{
				design: loadedConfig.design,
				heads: loadedConfig.heads,
				general: loadedConfig.general,
				selectedHead: null,
				projectName: '',
				headTableVisible: false,
			}
		));
	}

	return result;
};
